/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useTranslation } from 'react-i18next';
import { Box, Select, SelectChangeEvent, Snackbar, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MRT_ColumnDef } from 'material-react-table';

import { TeamAssignmentActionType } from '../../../../../API';
import { DropdownOptions } from '../../AllSchedules/AllSchedules.Model';
import { JobManagementType } from '../AllJobs.Model';
import AllJobsApiService from '../../../../Jobs/Services/JobManagementService';
import { IJobOverview } from '../../../../Jobs/Components/JobsListView/JobsListView';
import { TaskData } from '../../../../Jobs/Components/WorkTasks/WorkTasks';
import { IJobs } from '../../../../Jobs/Models/JobsView.Model';
import { IBidList } from '../../../../Jobs/Components/JobDetails/OneTimeJobDetails';
import { IProfileCardProps } from '../../../../Customer/Components/ViewFacilityDetails/ViewFacilityDetails';
import {
  CurrencySymbol,
  UserTypes,
  PopupSource,
  NumberOfRowsOptions,
  Paths,
} from '../../../../../Shared/Constants/App';
import DataGrid from '../../../../../Shared/Components/Common/DataGrid/DataGrid';
import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import DeletePopup from '../../../../../Shared/Components/Common/DeletePopup/DeletePopup';
import AssignUser from './AssignUser';
import ActionDropdown from './ActionDropdown';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import { Labels } from '../../../../../Shared/Components/Common/GraphContainer/GraphContainer';
import { getStoredCustomerDetails } from '../../../../../Shared/Utilities/utils';

import { Icons } from '../../../../../Shared/Constants/Icons';

import { ModifyButtonContainer, PaginationStyle, wrapperStyles } from '../../AllSchedules/AllJobs.Style';
import { CustomerContainer } from '../../../../../Shared/Components/Layout/styles';
import { StyledPagination } from '../../../../../Shared/Components/Common/CommonStyle/Pagination.Style';
import { isEmpty } from 'lodash';
import { Options } from '../../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { DropDown, SelectInput } from '../../../../../Shared/Components/Common/GraphContainer/GraphContainer.styles';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';

const ManageAssociatesAndSupervisors = (): JSX.Element => {
  const location = useLocation();
  const jobsData = location?.state?.selectedRows;
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<(typeof UserTypes)[keyof typeof UserTypes] | null>(null);
  const [popupSource, setPopupSource] = useState<PopupSource | null>(null);
  const [popupType, setPopupType] = useState<PopupSource | null>(null);
  const [vendorPopupMessage, setVendorPopupMessage] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isSchedulesLoading, setIsSchedulesLoading] = useState<boolean>(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isValueOpen, setValueOpen] = useState(false);
  const [workOrderIds, setWorkOrderIds] = useState<string[]>([]);
  const { t } = useTranslation(['jobManagement']);
  const { userRemoveContainer } = wrapperStyles;
  const navigate = useNavigate();
  const { userId } = getStoredCustomerDetails() || {};
  const toggleValue = () => {
    setValueOpen(!isValueOpen);
  };

  const fetchAllSchedules = async (
    currentPage: number,
    limit: number,
    woScheduleMasterIds: string[] | null,
    workOrderIds?: string[]
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ) => {
    const isReassigningVendor = location.state?.isReassigningVendor;
    setIsSchedulesLoading(true);
    try {
      const response = await AllJobsApiService.allSchedules(currentPage, limit, userId, {
        woScheduleMasterId: woScheduleMasterIds || undefined,
        workOrderId: workOrderIds ? workOrderIds : undefined,
        refreshCache: isReassigningVendor ? true : undefined,
      });
      if (response.data.data) {
        const filteredData = woScheduleMasterIds
          ? response.data.data.filter((schedule: any) => woScheduleMasterIds.includes(schedule.woScheduleMasterId))
          : workOrderIds
            ? response.data.data.filter((schedule: any) => workOrderIds.includes(schedule.workOrderId))
            : response.data.data;

        setTableData(filteredData);
      }
      setTotalCount(response?.data?.metadata?.totalCount);
    } catch (error) {
      console.error(t('messages:errorFetchingData'), error);
    } finally {
      setIsSchedulesLoading(false);
    }
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newItemsPerPage = Number(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    const storedWoScheduleMasterIds = localStorage.getItem('woScheduleMasterIds');
    const woScheduleMasterIds = storedWoScheduleMasterIds ? JSON.parse(storedWoScheduleMasterIds) : null;
    void fetchAllSchedules(currentPage, newItemsPerPage, woScheduleMasterIds);
  };

  const handleChange = (event: any, value: number): void => {
    setCurrentPage(value);
  };

  const handlePaginationChange = (event: any, value: number) => {
    handleChange(event, value);
    setCurrentPage(value);
    const storedWoScheduleMasterIds = localStorage.getItem('woScheduleMasterIds');
    const woScheduleMasterIds = storedWoScheduleMasterIds ? JSON.parse(storedWoScheduleMasterIds) : null;

    const storedWorkOrderIds = localStorage.getItem('workOrderIds');
    const workOrderIds = storedWorkOrderIds ? JSON.parse(storedWorkOrderIds) : null;
    void fetchAllSchedules(value, itemsPerPage, woScheduleMasterIds, workOrderIds);
  };

  const handleDeleteClick = (
    user: any,
    type: keyof typeof UserTypes,
    workOrderId?: string,
    woScheduleMasterId?: string,
    vendorId?: string,
    jobNumber?: string
  ) => {
    setSelectedUser([{ ...user, workOrderId, woScheduleMasterId, vendorId, jobNumber }]);
    setPopupSource(PopupSource.Icon);
    setIsDeletePopupVisible(true);
    setSelectedType(type);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleRemoveUser = async (popupSource: PopupSource) => {
    if (!selectedUser || selectedUser.length === 0) return;
    setIsLoading(true);
    const payload =
      popupSource === PopupSource.Action
        ? selectedUser
            .filter((userData: any) => userData?.userId)
            .map((userData: any) => ({
              workOrderId: userData?.workOrderId,
              workOrderScheduleMasterId: userData?.woScheduleMasterId,
              userId: userData?.userId,
              organizationId: userData?.vendorId,
              isSupervisor: selectedType === UserTypes.Supervisors,
              jobNumber: userData?.jobNumber,
            }))
        : selectedUser[0]?.userId
          ? [
              {
                workOrderId: selectedUser[0].workOrderId,
                workOrderScheduleMasterId: selectedUser[0].woScheduleMasterId,
                userId: selectedUser[0].userId,
                organizationId: selectedUser[0].vendorId,
                isSupervisor: selectedType === UserTypes.Supervisors,
                jobNumber: selectedUser?.[0]?.jobNumber,
              },
            ]
          : [];
    if (payload.length === 0) {
      setIsLoading(false);
      setIsDeletePopupVisible(false);
      return;
    }
    try {
      const response = await AllJobsApiService.manageScheduleAssignmentsEndpoint(
        payload,
        TeamAssignmentActionType.Remove,
        userId
      );
      if (
        (response.errors && response.errors.length > 0) ||
        (response.data?.manageScheduleAssignmentsFailure &&
          response.data.manageScheduleAssignmentsFailure.length > 0) ||
        response.data?.manageScheduleAssignmentsSuccess === null
      ) {
        const errorMessages =
          response.errors?.map((error: { message: any }) => error.message).join(', ') || t('messages:errorOccurred');
        setSnackbarMessage(errorMessages);
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
      } else {
        const successMessage =
          selectedType === UserTypes.Associates
            ? t('jobManagement:successfullyRemovedAssociate')
            : t('jobManagement:successfullyRemovedSupervisor');
        setSnackbarMessage(successMessage);
        setSnackbarOpen(true);
        setSnackbarSeverity('success');
        const storedWoScheduleMasterIds = localStorage.getItem('woScheduleMasterIds');
        const woScheduleMasterIds = storedWoScheduleMasterIds ? JSON.parse(storedWoScheduleMasterIds) : null;

        const storedWorkOrderIds = localStorage.getItem('workOrderIds');
        const workOrderIds = storedWorkOrderIds ? JSON.parse(storedWorkOrderIds) : null;
        fetchAllSchedules(currentPage, itemsPerPage, woScheduleMasterIds, workOrderIds);
      }
    } catch (error) {
      console.error(t('delete:anErrorOccurred'));
      setSnackbarMessage(t('delete:anErrorOccurred'));
      setSnackbarOpen(true);
      setSnackbarSeverity('error');
    } finally {
      setIsLoading(false);
      setIsDeletePopupVisible(false);
    }
  };

  const handleIconClick =
    (
      type: keyof typeof UserTypes,
      selectedRows?: any[],
      workOrderId?: string,
      woScheduleMasterId?: string,
      vendorId?: string,
      scheduleStartDate?: string,
      scheduleEndDate?: string,
      vendorNumber?: string,
      jobEndDate?: string,
      jobNumber?: string,
      scheduleName?: string
    ) =>
    () => {
      const uniqueVendorIds = new Set(selectedRows?.map((row) => row.vendorId));
      if (uniqueVendorIds.size > 1) {
        setVendorPopupMessage(t('jobManagement:vendorValidationMessage'));
        setPopupType(PopupSource.VendorValidation);
        setIsDeletePopupVisible(true);
        return;
      }
      setIsDropdownOpen(true);
      setSelectedUser(null);
      setSelectedType(type);
      let selectedRowsData;
      if (selectedRows) {
        selectedRowsData = selectedRows.map((row) => ({
          workOrderId: row?.workOrderId,
          woScheduleMasterId: row?.woScheduleMasterId,
          vendorId: row?.vendorId,
          scheduleStartDate: row?.scheduleStartDate,
          scheduleEndDate: row?.scheduleEndDate,
          vendorNumber: row?.vendorNumber,
          jobEndDate: row?.jobEndDate,
          jobNumber: row?.jobNumber,
          scheduleName: row?.scheduleName,
        }));
      } else if (workOrderId && woScheduleMasterId) {
        selectedRowsData = [
          {
            workOrderId,
            woScheduleMasterId,
            vendorId,
            scheduleStartDate,
            scheduleEndDate,
            vendorNumber,
            jobEndDate,
            jobNumber,
            scheduleName,
          },
        ];
      } else {
        console.error(t('messages:errorFetchingData'));
        return;
      }
      navigate(`/all-jobs/manage-associates-and-supervisors/${type}`, {
        state: {
          type,
          selectedRowsData,
        },
      });
    };

  const actions = [
    {
      value: DropdownOptions.RemoveSupervisors,
      label: t('jobManagement:removeSupervisors'),
      onClick: () => {
        const supervisorData = selectedRows.flatMap((row) =>
          row.supervisors
            ? row.supervisors.map((supervisor: any) => ({
                ...supervisor,
                workOrderId: row?.workOrderId,
                vendorId: row?.vendorId,
                jobNumber: row?.jobNumber,
                scheduleName: row?.scheduleName,
              }))
            : []
        );
        setSelectedUser(supervisorData);
        setPopupSource(PopupSource.Action);
        setIsDeletePopupVisible(true);
        setSelectedType(UserTypes.Supervisors);
      },
    },
    {
      value: DropdownOptions.RemoveAssociates,
      label: t('jobManagement:removeAssociates'),
      onClick: () => {
        const associateData = selectedRows.flatMap((row) =>
          row.associates
            ? row.associates.map((associate: any) => ({
                ...associate,
                workOrderId: row?.workOrderId,
                vendorId: row?.vendorId,
                jobNumber: row?.jobNumber,
                scheduleName: row?.scheduleName,
              }))
            : []
        );
        setSelectedUser(associateData);
        setPopupSource(PopupSource.Action);
        setIsDeletePopupVisible(true);
        setSelectedType(UserTypes.Associates);
      },
    },
    {
      value: DropdownOptions.AssignSupervisors,
      label: t('jobManagement:assignSupervisors'),
      onClick: handleIconClick(UserTypes.Supervisors as keyof typeof UserTypes, selectedRows),
    },
    {
      value: DropdownOptions.AssignAssociates,
      label: t('jobManagement:assignAssociates'),
      onClick: handleIconClick(UserTypes.Associates as keyof typeof UserTypes, selectedRows),
    },
  ];

  const renderCell = useCallback((renderedCellValue: React.ReactNode): string | number => {
    if (typeof renderedCellValue === 'string' || typeof renderedCellValue === 'number') {
      return renderedCellValue ? renderedCellValue : '-';
    }
    if (renderedCellValue && Array.isArray(renderedCellValue)) {
      return renderedCellValue[0]?.finalQuote || renderedCellValue[0]?.finalQuote === 0
        ? `${CurrencySymbol.Dollar}${Number(renderedCellValue[0]?.finalQuote).toFixed(2)}`
        : '-';
    }
    return '-';
  }, []);

  useEffect(() => {
    if (jobsData && jobsData.length > 0) {
      const woScheduleMasterIds = jobsData.map((job: any) => job?.woScheduleMasterId).filter(Boolean);
      localStorage.setItem('woScheduleMasterIds', JSON.stringify(woScheduleMasterIds));
    }
    if (!isEmpty(location?.state?.workOrderIds)) {
      const workOrderIds = location?.state?.workOrderIds ?? '';
      localStorage.setItem('workOrderIds', JSON.stringify(workOrderIds));
    }
    if (!isEmpty(location?.state?.woScheduleMasterId)) {
      const woScheduleMasterId = location?.state?.woScheduleMasterId ?? '';
      localStorage.setItem('woScheduleMasterIds', JSON.stringify(woScheduleMasterId));
    }
  }, []);

  useEffect(() => {
    const storedWoScheduleMasterIds = localStorage.getItem('woScheduleMasterIds');
    const woScheduleMasterIds = storedWoScheduleMasterIds ? JSON.parse(storedWoScheduleMasterIds) : null;

    const storedWorkOrderIds = localStorage.getItem('workOrderIds');
    const workOrderIds = storedWorkOrderIds ? JSON.parse(storedWorkOrderIds) : null;
    setWorkOrderIds(workOrderIds);
    fetchAllSchedules(currentPage, itemsPerPage, woScheduleMasterIds, workOrderIds);
  }, [currentPage, itemsPerPage, jobsData]);

  const columns = useMemo<MRT_ColumnDef<IJobOverview | TaskData | IJobs | IBidList | IProfileCardProps>[]>(
    () => [
      {
        accessorKey: 'jobNumber',
        header: 'Job Number',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'jobName',
        header: 'Job Name',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'jobAddress',
        header: 'Job Address',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'scheduleName',
        header: 'Schedule Name',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'vendorName',
        header: 'Vendor Name',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'supervisorName',
        header: 'Supervisors',
        enableSorting: false,
        enableColumnFilter: false,
        size: 300,
        Cell: ({ row }) => {
          const supervisors = row?.original?.supervisors as { name: string | null }[];
          const workOrderId = row?.original?.workOrderId;
          const woScheduleMasterId = row?.original?.woScheduleMasterId;
          const vendorId = row?.original?.vendorId;
          const scheduleStartDate = row?.original?.scheduleStartDate;
          const scheduleEndDate = row?.original?.scheduleEndDate;
          const vendorNumber = row?.original?.vendorNumber;
          const jobEndDate = row?.original?.jobEndDate;
          const jobNumber = row?.original?.jobNumber;
          const scheduleName = row?.original?.scheduleName;
          return (
            <ModifyButtonContainer style={{ gap: '0.5rem' }}>
              <div style={{ width: '90%' }}>
                {supervisors?.length ? (
                  supervisors
                    .filter((supervisor) => supervisor.name)
                    .map((supervisor, index) => (
                      <div key={index} style={userRemoveContainer}>
                        <span>{supervisor.name}</span>
                        <img
                          src={Icons.InfoWindowCloseIcon}
                          style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                          onClick={() =>
                            handleDeleteClick(
                              supervisor,
                              UserTypes.Supervisors as keyof typeof UserTypes,
                              workOrderId,
                              woScheduleMasterId,
                              vendorId,
                              jobNumber
                            )
                          }
                        />
                      </div>
                    ))
                ) : (
                  <span>-</span>
                )}
              </div>
              <div style={{ cursor: 'pointer' }}>
                <img
                  src={Icons.AddTagIcon}
                  onClick={handleIconClick(
                    UserTypes.Supervisors as keyof typeof UserTypes,
                    undefined,
                    workOrderId,
                    woScheduleMasterId,
                    vendorId,
                    scheduleStartDate,
                    scheduleEndDate,
                    vendorNumber,
                    jobEndDate,
                    jobNumber,
                    scheduleName
                  )}
                />
              </div>
            </ModifyButtonContainer>
          );
        },
      },
      {
        accessorKey: 'associates',
        header: 'Associates',
        enableSorting: false,
        enableColumnFilter: false,
        size: 300,
        Cell: ({ row }) => {
          const associates = row?.original?.associates as { name: string | null }[];
          const workOrderId = row?.original?.workOrderId;
          const woScheduleMasterId = row?.original?.woScheduleMasterId;
          const vendorId = row?.original?.vendorId;
          const scheduleStartDate = row?.original?.scheduleStartDate;
          const scheduleEndDate = row?.original?.scheduleEndDate;
          const vendorNumber = row?.original?.vendorNumber;
          const jobEndDate = row?.original?.jobEndDate;
          const jobNumber = row?.original?.jobNumber;
          const scheduleName = row?.original?.scheduleName;
          return (
            <ModifyButtonContainer style={{ gap: '0.5rem' }}>
              <div style={{ width: '90%' }}>
                {associates?.length ? (
                  associates
                    .filter((associate) => associate.name)
                    .map((associate, index) => (
                      <div key={index} style={userRemoveContainer}>
                        <span>{associate.name}</span>
                        <img
                          src={Icons.InfoWindowCloseIcon}
                          style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                          onClick={() =>
                            handleDeleteClick(
                              associate,
                              UserTypes.Associates as keyof typeof UserTypes,
                              workOrderId,
                              woScheduleMasterId,
                              vendorId,
                              jobNumber
                            )
                          }
                        />
                      </div>
                    ))
                ) : (
                  <span>-</span>
                )}
              </div>
              <div style={{ cursor: 'pointer' }}>
                <img
                  src={Icons.AddTagIcon}
                  onClick={handleIconClick(
                    UserTypes.Associates as keyof typeof UserTypes,
                    undefined,
                    workOrderId,
                    woScheduleMasterId,
                    vendorId,
                    scheduleStartDate,
                    scheduleEndDate,
                    vendorNumber,
                    jobEndDate,
                    jobNumber,
                    scheduleName
                  )}
                />
              </div>
            </ModifyButtonContainer>
          );
        },
      },
    ],
    []
  );

  const navigateToAllJobs = () => {
    navigate(Paths.ManageAllJobs);
  };

  const handleCreateSchedule = (rowData: any) => {
    navigate(Paths.CreateJobSchedule, {
      state: {
        selectedRows: [rowData],
        isReassignVendor: true,
      },
    });
  };

  return (
    <>
      <PageTitle title={t('jobManagement:manageAssociatesAndSupervisors')} />
      <CustomerContainer>
        <ModifyButtonContainer
          sx={{
            paddingRight: '0.5rem',
          }}>
          {selectedRows.length > 0 && <ActionDropdown options={actions} />}
        </ModifyButtonContainer>
        <Box sx={{ marginTop: '1rem' }}>
          <DataGrid
            columns={columns}
            data={tableData || []}
            enableRowSelect={true}
            enableColumnPinning={false}
            setSelectedRows={setSelectedRows}
            selectedRows={selectedRows}
            configureColumns={false}
            loader={isSchedulesLoading}
            type={JobManagementType.AllSchedules}
          />
          {!isLoading && totalCount > 10 && (
            <PaginationStyle>
              <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
                <StyledPagination
                  count={Math.ceil(totalCount / itemsPerPage)}
                  page={currentPage}
                  onChange={(event, value) => {
                    handlePaginationChange(event, value);
                  }}
                  color="primary"
                />
              </Stack>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '3rem' }}>
                <Typography>
                  {t('dashboard:totalItems')}: {totalCount}
                </Typography>
                {totalCount > 10 && (
                  <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <Typography>{t('dashboard:recordsPerPage')}:</Typography>
                    <DropDown>
                      <Box onClick={toggleValue}>
                        <Select
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          label="Records"
                          input={<SelectInput />}
                          IconComponent={() => (
                            <img
                              src={Icons.DropdownIcon}
                              style={{
                                transform: isValueOpen ? Labels.Rotate : 'none',
                                position: 'absolute',
                                right: '0.75rem',
                                cursor: 'pointer',
                              }}
                            />
                          )}
                          open={isValueOpen}
                          onClose={toggleValue}
                          onOpen={toggleValue}>
                          {NumberOfRowsOptions.map((dataItem) => (
                            <Options key={dataItem.id} value={dataItem.value}>
                              {dataItem.label}
                            </Options>
                          ))}
                        </Select>
                      </Box>
                    </DropDown>
                  </Box>
                )}
              </Box>
            </PaginationStyle>
          )}
        </Box>
        {workOrderIds?.length && (
          <ModifyButtonContainer sx={{ marginTop: '2rem', gap: '2rem' }}>
            <Button
              type={ButtonType.Primary}
              label={t('jobManagement:createJobSchedule')}
              onClick={() => handleCreateSchedule(location?.state?.jobsData)}
            />
            <Button
              type={ButtonType.Primary}
              label={t('jobManagement:backToAllJobs')}
              onClick={() => navigateToAllJobs()}
            />
          </ModifyButtonContainer>
        )}
        <DeletePopup
          open={isDeletePopupVisible}
          onClose={() => {
            setIsDeletePopupVisible(false);
            setPopupSource(null);
            setPopupType(null);
          }}
          message={
            popupType === PopupSource.VendorValidation
              ? vendorPopupMessage
              : `${selectedType === UserTypes.Associates ? t('assignJob:areYouSureToRemoveAssociate') : t('assignJob:areYouSureToRemoveSupervisor')}`
          }
          primaryButtonLabel={
            popupType === PopupSource.VendorValidation ? t('jobManagement:ok') : t('jobManagement:yes')
          }
          name={''}
          isPrimaryButtonLoading={isLoading}
          primaryAction={() => {
            if (popupType === PopupSource.VendorValidation) {
              setIsDeletePopupVisible(false);
              setPopupType(null);
            } else if (popupSource === PopupSource.Action) {
              handleRemoveUser(PopupSource.Action);
            } else if (popupSource === PopupSource.Icon) {
              handleRemoveUser(PopupSource.Icon);
            }
            setPopupSource(null);
            setPopupType(null);
          }}
          secondaryButtonLabel={popupType === PopupSource.VendorValidation ? '' : t('jobManagement:no')}
          secondaryAction={() => {
            setIsDeletePopupVisible(false);
            setPopupSource(null);
            setPopupType(null);
          }}
        />
        {isDropdownOpen && <AssignUser type={selectedType} />}
      </CustomerContainer>
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default ManageAssociatesAndSupervisors;
